// The mini build of zod: these schemas also run in the browser, where the full build would be most
// of what a visitor downloads.
import * as z from "zod/mini";
import { ADDRESS_HOST_KEYS, formatAddress } from "../address.js";
// Contract: docs/specs/rest.md. The server builds these shapes; clients parse with these schemas.
// Absent values are `null`, never missing keys. Changes within a version are additive.
/** One prefix per operation, so that the address keeps the rest of the path to itself. */
export const REST_ROUTES = {
    mounts: "/api/v1/mounts",
    browse: "/api/v1/browse",
    find: "/api/v1/find",
    /** A search across every pack the deployment serves (ADR-0070). */
    search: "/api/v1/search",
    skills: "/api/v1/skills",
    files: "/api/v1/files",
    /** The bytes of a file as the pack has them, as an attachment, where the deployment serves them itself (ADR-0077). */
    raw: "/api/v1/raw",
    featured: "/api/v1/featured",
    showcase: "/api/v1/showcase",
    legal: "/api/v1/legal",
    /** The page of an account: `/api/v1/owners/gh/<owner>` (account.ts). */
    owners: "/api/v1/owners",
    /** Whoever is signed in, and what is theirs (account.ts). */
    me: "/api/v1/me",
    /** What a client asked to be authorized for, and the person's answer (account.ts). */
    authorization: "/api/v1/oauth/request",
    decision: "/api/v1/oauth/decision",
    /** A command that asks to connect with a code a person approves (account.ts). */
    connect: "/api/v1/connect",
};
/** How many skills and how many documents a mount overview lists. The counts are complete. */
export const REST_MOUNT_LIST_LIMIT = 200;
/** How many skill names a featured address shows. */
export const REST_FEATURED_SKILL_NAMES = 5;
/** The REST path of an operation on an address, without a query string. */
export function restPath(operation, address) {
    return `${REST_ROUTES[operation]}${formatAddress(address)}`;
}
const count = z.int().check(z.nonnegative());
const indexing = z.object({ status: z.literal("indexing") });
const failed = z.object({ status: z.literal("failed"), errorCode: z.string() });
/**
 * The newest commit of the ref while another index is served in its place (ADR-0068): being
 * indexed, or failed with the index's own `errorCode`. The same commit as the one served means
 * that commit's index is being rebuilt under newer reading rules.
 */
export const restNewestSchema = z.object({
    commit: z.string(),
    status: z.enum(["indexing", "failed"]),
    errorCode: z.nullable(z.string()),
});
export const restDiagnosticSchema = z.object({
    path: z.string(),
    code: z.string(),
    message: z.string(),
});
export const restReferenceSchema = z.object({
    source: z.optional(z.string()),
    href: z.string(),
    path: z.string(),
    status: z.enum(["available", "outside_mount", "missing", "blocked"]),
});
/** The license a skill or a repository carries, as the index classified it (ADR-0026). */
export const restLicenseSchema = z.object({
    kind: z.enum(["permissive", "restrictive", "none"]),
    /** The SPDX identifier when recognized, else the declared text; `null` for none. */
    name: z.nullable(z.string()),
    /** The repository-root path of the license file or manifest that said so; `null` for none. */
    source: z.nullable(z.string()),
});
export const restBrowseEntrySchema = z.object({
    browsePath: z.optional(z.string()),
    overviewPath: z.optional(z.string()),
    /** A skill's license. */
    license: z.optional(restLicenseSchema),
    /** True for a skill this mount describes without serving its content. */
    describedOnly: z.optional(z.boolean()),
    kind: z.enum(["directory", "skill", "file"]),
    path: z.string(),
    name: z.nullable(z.string()),
    description: z.nullable(z.string()),
    skillCount: count,
    documentCount: count,
    /** How many pictures the folder holds; absent when none, and from an answer of a deployment older than the field. */
    imageCount: z.optional(count),
    size: z.nullable(count),
    manifestPath: z.nullable(z.string()),
    language: z.nullable(z.string()),
});
export const restFolderOverviewSchema = z.object({
    path: z.string(),
    title: z.nullable(z.string()),
    description: z.nullable(z.string()),
});
/** A picture a document links to, with its alternative text, empty when it has none. */
export const restDocumentImageSchema = z.object({
    path: z.string(),
    alt: z.string(),
});
/**
 * What a document says it was made from (ADR-0076): its front-matter `source` as written and,
 * when it names a file of the repository, the file's `path` and the `url` of its bytes.
 */
export const restDocumentSourceSchema = z.object({
    text: z.string(),
    path: z.optional(z.string()),
    url: z.optional(z.string()),
});
/** One heading of a file's outline, with the `offset` and `length` that read its section. */
export const restOutlineHeadingSchema = z.object({
    level: z.int().check(z.gte(1), z.lte(6)),
    /** Plain text; empty for a heading without usable text. */
    text: z.string(),
    offset: count,
    length: count,
});
/** The outline of a file the browse route was given the path of (ADR-0063). */
export const restFileOutlineSchema = z.object({
    path: z.string(),
    /** Bytes, as the tree has the file. */
    size: count,
    /** Characters of the file as it is read, or `null` when its text is not at hand. */
    totalLength: z.nullable(count),
    headings: z.array(restOutlineHeadingSchema),
    /** True when the file has more headings than an outline keeps. */
    truncated: z.boolean(),
    /** Why there are no headings to list, when there are none for a reason other than the file's own. */
    unavailable: z.optional(z.enum(["not_markdown", "not_text", "too_large", "described_only"])),
    license: z.optional(restLicenseSchema),
    sourceUrl: z.optional(z.string()),
    /** With `not_text`: a Git LFS pointer to a file of this size, which `sourceUrl` serves (ADR-0077). */
    lfs: z.optional(z.object({ size: count })),
    /** For a Markdown document: the pictures it links to, each with its alternative text. */
    images: z.optional(z.array(restDocumentImageSchema)),
    /** For a Markdown document: what it says it was made from (ADR-0076). */
    sources: z.optional(z.array(restDocumentSourceSchema)),
});
export const restBrowseSchema = z.discriminatedUnion("status", [
    z.object({
        status: z.literal("ready"),
        commit: z.string(),
        path: z.string(),
        overview: z.optional(restFolderOverviewSchema),
        /** The repository manifest of the browsed folder, when it has one of its own. */
        manifestPath: z.optional(z.string()),
        /** When `path` names a file: its outline, and `entries` is empty. */
        outline: z.optional(restFileOutlineSchema),
        entries: z.array(restBrowseEntrySchema),
        diagnostics: z.optional(z.array(restDiagnosticSchema)),
        nextCursor: z.nullable(z.string()),
    }),
    indexing,
    failed,
]);
export const restRepositorySchema = z.object({
    host: z.enum(ADDRESS_HOST_KEYS),
    /** As the host spells them, for display. The address has the canonical lowercase form. */
    owner: z.string(),
    name: z.string(),
    defaultBranch: z.string(),
    /** What the host shows as the repository's description, or `null`. */
    description: z.nullable(z.string()),
    /**
     * The owner's picture as the host serves it: an https URL, loaded by the browser (ADR-0031);
     * the empty string for an owner with no picture anywhere, as the one of a pack served from a
     * directory of the deployment (ADR-0071).
     */
    avatar: z.string(),
    /**
     * Whether everyone can see the repository or only the people the host lets
     * (specs/permissions.md). A `private` one is only ever answered to someone who signed in.
     */
    visibility: z.optional(z.enum(["public", "private"])),
});
/** What people see in one language instead of a skill's name and description. */
export const restSkillTranslationSchema = z.object({
    title: z.nullable(z.string()),
    description: z.nullable(z.string()),
});
/** By language tag, such as `ko`. Absent languages are simply not there. */
export const restSkillTranslationsSchema = z.record(z.string(), restSkillTranslationSchema);
/** What people see in one language instead of a repository's name and description. */
export const restRepoTranslationSchema = z.object({
    name: z.nullable(z.string()),
    description: z.nullable(z.string()),
});
export const restRepoTranslationsSchema = z.record(z.string(), restRepoTranslationSchema);
export const restSkillSummarySchema = z.object({
    path: z.optional(z.string()),
    name: z.string(),
    directory: z.string(),
    description: z.string(),
    warnings: z.array(z.string()),
    translations: restSkillTranslationsSchema,
});
export const restDocumentSummarySchema = z.object({
    path: z.string(),
    title: z.nullable(z.string()),
    summary: z.nullable(z.string()),
});
/** What the repository manifest (SKILLCDN.md) says about the mount. */
export const restManifestSchema = z.object({
    /** The canonical repository-root path of the applicable manifest. */
    path: z.nullable(z.string()),
    /** `null`: the repository goes by the name its git host gives it. */
    name: z.nullable(z.string()),
    description: z.string(),
    /** The tag of the language the repository says it is written in, or `null`. */
    language: z.nullable(z.string()),
    translations: restRepoTranslationsSchema,
});
/**
 * How the mount is searched right now (ADR-0064): by words alone, or by words and meaning, and
 * where the embedding of its index stands: `null` where nothing has embedded it or nothing counts
 * it, complete, or how many of the documents to embed have their vectors so far.
 */
export const restMountSearchSchema = z.object({
    meaning: z.boolean(),
    embedding: z.nullable(z.discriminatedUnion("status", [
        z.object({ status: z.literal("complete") }),
        z.object({ status: z.literal("in_progress"), embedded: count, total: count }),
    ])),
});
export const restMountSchema = z.object({
    address: z.string(),
    repository: restRepositorySchema,
    ref: z.nullable(z.string()),
    pinned: z.boolean(),
    commit: z.string(),
    path: z.string(),
    verified: z.boolean(),
    /**
     * The picture that stands for the address (ADR-0031): the operator's, else the one the
     * manifest declares, as a URL the browser loads it from; `null` when there is none.
     */
    image: z.nullable(z.string()),
    /**
     * The ref's newest commit while `commit` is another one, served in its place, or while this
     * commit's index is rebuilt (ADR-0068); `null` when what is served is the newest. Absent from
     * an answer of a deployment older than the field.
     */
    newest: z.optional(z.nullable(restNewestSchema)),
    index: z.discriminatedUnion("status", [
        z.object({
            status: z.literal("ready"),
            truncated: z.boolean(),
            /** The license that governs the mounted directory outside its skills. */
            license: z.optional(restLicenseSchema),
            /** `null` when the mount has no manifest. */
            manifest: z.nullable(restManifestSchema),
            overview: z.optional(restFolderOverviewSchema),
            skillCount: count,
            documentCount: count,
            skills: z.array(restSkillSummarySchema),
            documents: z.array(restDocumentSummarySchema),
            diagnostics: z.array(restDiagnosticSchema),
            groups: z.optional(z.array(restBrowseEntrySchema)),
            /** Absent from an answer of a deployment older than the field. */
            search: z.optional(restMountSearchSchema),
        }),
        indexing,
        failed,
    ]),
});
/** A section of a document that a search matched (ADR-0063). */
export const restFindSectionSchema = z.object({
    /** The heading path, outermost first; empty for the text before the first heading. */
    heading: z.array(z.string()),
    /** What reads the section through the files route: `offset` and `limit`. */
    offset: count,
    length: count,
    /** A glimpse of the passage as plain text, or `null` when the section has no prose. */
    excerpt: z.nullable(z.string()),
});
export const restFindItemSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("skill"),
        path: z.optional(z.string()),
        name: z.string(),
        directory: z.string(),
        description: z.string(),
        translations: restSkillTranslationsSchema,
        /** With a query: the files of the skill that matched as well, best first. */
        files: z.array(restDocumentSummarySchema),
        /** How many more of its files matched than are listed. */
        moreFiles: count,
        license: z.optional(restLicenseSchema),
        /** True when this mount describes the skill without serving its content. */
        describedOnly: z.optional(z.boolean()),
    }),
    z.object({
        kind: z.literal("document"),
        path: z.string(),
        title: z.nullable(z.string()),
        summary: z.nullable(z.string()),
        /** The directory of the skill the document belongs to, or `null`. */
        skillDirectory: z.nullable(z.string()),
        /** With a query: the sections that matched, best first; absent for a document described only. */
        sections: z.optional(z.array(restFindSectionSchema)),
        /** True for the README of a folder, the map that says a little of everything in it. */
        overview: z.optional(z.boolean()),
    }),
]);
export const restFindSchema = z.discriminatedUnion("status", [
    z.object({
        status: z.literal("ready"),
        query: z.nullable(z.string()),
        commit: z.optional(z.string()),
        path: z.optional(z.string()),
        nextCursor: z.optional(z.nullable(z.string())),
        items: z.array(restFindItemSchema),
        diagnostics: z.optional(z.array(restDiagnosticSchema)),
        /** Without a query: how many skills, and how many documents outside the skills, there are. */
        totals: z.nullable(z.object({ skills: count, documents: count })),
        /**
         * With a query, where the search by meaning is at hand (ADR-0064): whether the sections
         * nearest in meaning were fused with the words' results, or the words answered alone this
         * time, and whether the first results were reranked. Absent where only words rank.
         */
        ranking: z.optional(z.object({ meaning: z.boolean(), reranked: z.boolean() })),
    }),
    indexing,
    failed,
]);
/**
 * What a pack a search found something in is, for the result to say so: a pack of skills (a
 * manifest and skills), a pack of documents (a manifest and no skill), or a repository read
 * without a manifest; whether the operator vouches for it; and how many skills it holds.
 */
export const restHubPackSchema = z.object({
    kind: z.enum(["skills", "documents", "repository"]),
    verified: z.boolean(),
    skills: count,
    /** The owner's picture as the git host serves it; empty for a pack at no git host. */
    avatar: z.optional(z.string()),
});
/** One result of a search across packs (ADR-0070): the item, and the pack it is in. */
export const restHubItemSchema = z.object({
    /** The canonical address of the pack, `/gh/<owner>/<repo>`. */
    address: z.string(),
    /** `owner/name` as the host spells it. */
    repository: z.string(),
    /** What the pack is; absent from an answer of a deployment older than the field. */
    pack: z.optional(restHubPackSchema),
    item: restFindItemSchema,
});
/** What `GET /api/v1/search` answers: the items that matched across the packs searched. */
export const restSearchSchema = z.object({
    status: z.literal("ready"),
    query: z.string(),
    /** How many packs were searched. */
    packs: count,
    items: z.array(restHubItemSchema),
    /** Where the search by meaning is at hand: as the `find` route says it. */
    ranking: z.optional(z.object({ meaning: z.boolean(), reranked: z.boolean() })),
});
export const restSkillSchema = z.discriminatedUnion("status", [
    z.object({
        status: z.literal("ready"),
        /** The commit the skill was read from; every result names one. */
        commit: z.optional(z.string()),
        skill: z.object({
            path: z.optional(z.string()),
            ruleChain: z.optional(z.array(z.object({ path: z.string(), body: z.string(), truncated: z.boolean() }))),
            references: z.optional(z.array(restReferenceSchema)),
            complete: z.optional(z.boolean()),
            nextCursor: z.optional(z.nullable(z.string())),
            includedContents: z.optional(z.array(z.object({ path: z.string(), content: z.nullable(z.string()), truncated: z.boolean() }))),
            name: z.string(),
            directory: z.string(),
            description: z.string(),
            license: z.nullable(z.string()),
            compatibility: z.nullable(z.string()),
            allowedTools: z.nullable(z.string()),
            metadata: z.record(z.string(), z.string()),
            body: z.string(),
            files: z.array(z.string()),
            filesTruncated: z.boolean(),
            /**
             * Files of the skill over the read limit (ADR-0033): not readable through the files
             * endpoint, with `sourceUrl` where their bytes are at the host.
             */
            oversized: z.optional(z.array(z.object({ path: z.string(), size: count, sourceUrl: z.string() }))),
            /** The files the skill declares as needed on every run; `get_skill` returns their text. */
            included: z.array(z.string()),
            warnings: z.array(z.string()),
            translations: restSkillTranslationsSchema,
            /**
             * How the skill is served, from the license it carries (ADR-0026). With `full` false the
             * body, rules and files are withheld, and `sourceUrl` is where to read the skill instead.
             */
            serving: z.optional(z.object({ license: restLicenseSchema, full: z.boolean(), sourceUrl: z.string() })),
            /** The repository's rules from its manifest, or `null` when there are none. */
            rules: z.nullable(z.object({
                /** The canonical repository-root path of the applicable manifest. */
                path: z.nullable(z.string()),
                body: z.string(),
                truncated: z.boolean(),
            })),
        }),
    }),
    indexing,
    failed,
]);
export const restDirectoryEntrySchema = z.object({
    path: z.string(),
    kind: z.enum(["file", "directory"]),
    /** Bytes, for a file. */
    size: z.nullable(count),
});
/** A page of a file, or the entries of a directory when the path names one. */
export const restFileSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("file"),
        /** The commit the file was read from; every result names one. */
        commit: z.optional(z.string()),
        references: z.optional(z.array(restReferenceSchema)),
        /** On the first page of a Markdown document: what it says it was made from (ADR-0076). */
        sources: z.optional(z.array(restDocumentSourceSchema)),
        path: z.string(),
        content: z.string(),
        offset: count,
        nextOffset: z.nullable(count),
        totalLength: count,
    }),
    z.object({
        kind: z.literal("directory"),
        commit: z.optional(z.string()),
        /** Empty for the mounted root. */
        path: z.string(),
        entries: z.array(restDirectoryEntrySchema),
        truncated: z.boolean(),
    }),
]);
/** A repository on a card: what the explorer features, and what the page of an account leads with. */
export const restRepositoryCardSchema = z.object({
    address: z.string(),
    repository: restRepositorySchema,
    /** The name and description the repository gives itself, once indexed, or `null`. */
    manifest: z.nullable(z.object({
        name: z.nullable(z.string()),
        description: z.string(),
        translations: restRepoTranslationsSchema,
    })),
    /** Whether the operator, or later the owner, vouches for the repository. */
    verified: z.boolean(),
    /** The picture that stands for the address, as on the mount, or `null`. */
    image: z.nullable(z.string()),
    status: z.enum(["ready", "indexing", "failed"]),
    skillCount: z.nullable(count),
    skills: z.array(z.string()),
});
export const restFeaturedSchema = z.object({
    items: z.array(restRepositoryCardSchema),
});
export const restErrorSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        /** With `skill.ambiguous`: the directories to choose from. */
        directories: z.optional(z.array(z.string())),
        /**
         * With `file.too_large`, `file.not_text` and `file.not_served`: where the file is at its
         * host. For the first two it is the raw bytes, which this endpoint cannot carry.
         */
        sourceUrl: z.optional(z.string()),
    }),
});
//# sourceMappingURL=contracts.js.map